/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

type PreviewRendererProps = {
	jsonDocument: DocNode;
	showTelepointer: boolean;
	providerFactory: ProviderFactory;
	reduceMargins?: boolean;
};

export const PreviewRenderer = ({
	jsonDocument,
	showTelepointer,
	providerFactory,
	reduceMargins,
}: PreviewRendererProps) => {
	const previewRendererRef = useRef<HTMLDivElement>(null);

	// override renderer's syntax highlighting styles so it appears consistent with the editor
	const styles = [
		css({
			// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.code-block .token.token': {
				fontStyle: 'normal',
				fontWeight: token('font.weight.regular'),
				color: token('color.text', N800),
			},
		}),
	];

	const reduceMarginStyles = css({
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.ak-renderer-document >:first-child': {
			marginTop: 0,
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.ak-renderer-document >:last-child': {
			marginBottom: 0,
		},
	});

	if (reduceMargins) {
		styles.push(reduceMarginStyles);
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
		<div data-testid="preview-renderer" css={styles} ref={previewRendererRef}>
			<RendererActionsContext>
				<ReactRenderer
					document={jsonDocument}
					media={{
						// We don't currently support images with captions
						allowLinking: false,
					}}
					// By default -- the renderer uses a "default schema" which supports all
					// the nodes and marks the markdown transformer can create.
					// see packages/editor/adf-schema/src/schema/default-schema.ts for details.
					useSpecBasedValidator
					addTelepointer={showTelepointer}
					dataProviders={providerFactory}
					allowPlaceholderText
				/>
			</RendererActionsContext>
		</div>
	);
};
